import React, { useState, useEffect } from 'react';
import eventService from '../services/eventService';
import pointsService from '../services/pointsService';
import AdminPanel from './AdminPanel';

const Dashboard = ({ user, onLogout }) => {
  const [activeTab, setActiveTab] = useState('events');
  const [events, setEvents] = useState([]);
  const [userBookings, setUserBookings] = useState([]);
  const [points, setPoints] = useState(0);
  const [pointHistory, setPointHistory] = useState([]);
  const [couponCode, setCouponCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load events
      const eventsResponse = await eventService.getAllEvents();
      if (eventsResponse.data.success) {
        console.log('Loaded events with descriptions:', eventsResponse.data.events);
        setEvents(eventsResponse.data.events);
      }

      // Load user points
      const pointsResponse = await pointsService.getPoints();
      if (pointsResponse.data.success) {
        setPoints(pointsResponse.data.points);
      }

      // Load point history
      const historyResponse = await pointsService.getPointHistory();
      if (historyResponse.data.success) {
        setPointHistory(historyResponse.data.history);
      }

      // Load user bookings
      const bookingsResponse = await eventService.getUserBookings();
      if (bookingsResponse.data.success) {
        setUserBookings(bookingsResponse.data.bookings);
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
      setMessage({ type: 'danger', text: 'Fehler beim Laden der Daten' });
    } finally {
      setLoading(false);
    }
  };

  const handleBookEvent = async (eventId, dateId) => {
    try {
      setLoading(true);
      const response = await eventService.bookEvent(eventId, dateId);
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Termin erfolgreich gebucht!' });
        loadDashboardData();
      }
    } catch (error) {
      setMessage({ 
        type: 'danger', 
        text: error.response?.data?.message || 'Buchung fehlgeschlagen' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async (eventId, dateId) => {
    try {
      setLoading(true);
      const response = await eventService.cancelBooking(eventId, dateId);
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Buchung erfolgreich storniert!' });
        loadDashboardData();
      }
    } catch (error) {
      setMessage({ 
        type: 'danger', 
        text: error.response?.data?.message || 'Stornierung fehlgeschlagen' 
      });
    } finally {
      setLoading(false);
    }
  };

  const handleApplyCoupon = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      const response = await pointsService.applyCoupon(couponCode);
      if (response.data.success) {
        setMessage({ type: 'success', text: 'Coupon erfolgreich eingelöst!' });
        setCouponCode('');
        loadDashboardData();
      }
    } catch (error) {
      setMessage({ 
        type: 'danger', 
        text: error.response?.data?.message || 'Fehler beim Einlösen des Coupons' 
      });
    } finally {
      setLoading(false);
    }
  };

  const canCancelBooking = (eventDate) => {
    const eventDateTime = new Date(eventDate);
    const now = new Date();
    const timeDiff = eventDateTime - now;
    const hoursUntilEvent = timeDiff / (1000 * 60 * 60);
    
    return hoursUntilEvent >= 0; // Kann bis zum Event storniert werden
  };

  const getRefundInfo = (eventDate) => {
    const eventDateTime = new Date(eventDate);
    const now = new Date();
    const timeDiff = eventDateTime - now;
    const hoursUntilEvent = timeDiff / (1000 * 60 * 60);
    
    if (hoursUntilEvent < 24) {
      return { canRefund: false, message: "⚠️ Stornierung möglich, aber keine Punkterückerstattung (weniger als 24h)" };
    }
    return { canRefund: true, message: "✅ Stornierung mit Punkterückerstattung möglich" };
  };

  const isUserBookedForDate = (event, eventDate) => {
    // Check if this specific event date is in user's bookings
    return userBookings.some(booking => 
      booking.event.id === event._id && 
      booking.event.dateId === eventDate._id
    );
  };

  const getUserBookingStatus = (event, eventDate) => {
    // Get booking status for this specific event date
    const booking = userBookings.find(booking => 
      booking.event.id === event._id && 
      booking.event.dateId === eventDate._id
    );
    return booking ? booking.status : null;
  };

  const canBookDate = (event, eventDate) => {
    if (!eventDate.isActive) return false;
    if (new Date(eventDate.date) <= new Date()) return false;
    if (points < event.pointsRequired) return false;
    
    // Check if user already booked this specific date using bookings data
    return !isUserBookedForDate(event, eventDate);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('de-DE', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const dismissMessage = () => {
    setMessage({ type: '', text: '' });
  };

  const renderEventCard = (event) => (
    <div key={event._id} className="card event-card">
      {event.image && (
        <img src={event.image} alt={event.name} className="event-image" />
      )}
      <div className="event-content">
        <h3 className="event-title">{event.name}</h3>
        <div className="event-description" dangerouslySetInnerHTML={{ __html: event.description }} />
        
        <div className="event-meta">
          <div className="event-meta-item">
            <span>💎</span>
            <span>{event.pointsRequired} Punkte</span>
          </div>
        </div>

        <div className="event-dates">
          <h4 style={{ marginBottom: '1rem', color: 'var(--primary-color)' }}>Verfügbare Termine:</h4>
          {event.dates
            .filter(date => date.isActive && new Date(date.date) > new Date())
            .sort((a, b) => new Date(a.date) - new Date(b.date))
            .map(eventDate => (
              <div key={eventDate._id} className="event-date-card" style={{
                border: '1px solid var(--border-color)',
                borderRadius: '8px',
                padding: '1rem',
                marginBottom: '1rem',
                background: 'white'
              }}>
                <div className="event-date-content" style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'flex-start',
                  gap: '1rem'
                }}>
                  <div>
                    <div style={{ fontWeight: '600', marginBottom: '0.5rem' }}>
                      {formatDate(eventDate.date)}
                    </div>
                    {eventDate.description && (
                      <div style={{ fontSize: '0.9rem', color: 'var(--primary-color)', marginBottom: '0.5rem', fontStyle: 'italic' }}>
                        📝 {eventDate.description}
                      </div>
                    )}
                    <div style={{ fontSize: '0.9rem', color: 'var(--text-light)' }}>
                      {eventDate.availableSpots} von {eventDate.maxParticipants} Plätzen frei
                    </div>
                    {eventDate.isFull && (
                      <div style={{ fontSize: '0.9rem', color: 'var(--warning-color)' }}>
                        Warteliste: {eventDate.waitlist.length} Personen
                      </div>
                    )}
                  </div>
                  
                  <div className="event-date-actions" style={{ display: 'flex', gap: '0.5rem', minWidth: 'fit-content' }}>
                    {(() => {
                      const bookingStatus = getUserBookingStatus(event, eventDate);
                      if (bookingStatus === 'confirmed') {
                        return (
                          <>
                            <span className="badge" style={{
                              background: 'var(--success-color)',
                              color: 'white',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.8rem'
                            }}>
                              Gebucht
                            </span>
                            {canCancelBooking(eventDate.date) ? (
                              <div className="event-cancel-actions" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem' }}>
                                {(() => {
                                  const refundInfo = getRefundInfo(eventDate.date);
                                  return (
                                    <>
                                      <button
                                        className="btn btn-danger btn-sm"
                                        onClick={() => {
                                          if (!refundInfo.canRefund) {
                                            const confirmCancel = window.confirm(
                                              `ACHTUNG: Weniger als 24 Stunden bis zum Termin!\n\n` +
                                              `Bei Stornierung werden die Punkte NICHT zurückerstattet.\n\n` +
                                              `Möchten Sie trotzdem stornieren?`
                                            );
                                            if (!confirmCancel) return;
                                          }
                                          handleCancelBooking(event._id, eventDate._id);
                                        }}
                                        disabled={loading}
                                        title={refundInfo.message}
                                      >
                                        Stornieren
                                      </button>
                                      <small style={{ 
                                        color: refundInfo.canRefund ? 'var(--success-color)' : 'var(--danger-color)', 
                                        fontSize: '0.7rem', 
                                        textAlign: 'right',
                                        maxWidth: '120px'
                                      }}>
                                        {refundInfo.canRefund ? '✅ Mit Rückerstattung' : '⚠️ Ohne Rückerstattung'}
                                      </small>
                                    </>
                                  );
                                })()}
                              </div>
                            ) : (
                              <small style={{ color: 'var(--text-light)', fontSize: '0.7rem' }}>
                                Event bereits vorbei
                              </small>
                            )}
                          </>
                        );
                      } else if (bookingStatus === 'waitlisted') {
                        return (
                          <>
                            <span className="badge" style={{
                              background: 'var(--warning-color)',
                              color: 'white',
                              padding: '0.25rem 0.5rem',
                              borderRadius: '4px',
                              fontSize: '0.8rem'
                            }}>
                              Warteliste
                            </span>
                            <button
                              className="btn btn-danger btn-sm"
                              onClick={() => handleCancelBooking(event._id, eventDate._id)}
                              disabled={loading}
                            >
                              Verlassen
                            </button>
                          </>
                        );
                      } else if (canBookDate(event, eventDate)) {
                        return (
                          <button
                            className="btn btn-primary btn-sm"
                            onClick={() => handleBookEvent(event._id, eventDate._id)}
                            disabled={loading}
                          >
                            {eventDate.isFull ? 'Warteliste' : 'Buchen'}
                          </button>
                        );
                      } else {
                        return (
                          <span style={{ 
                            fontSize: '0.8rem', 
                            color: 'var(--text-light)',
                            fontStyle: 'italic'
                          }}>
                            {points < event.pointsRequired ? 'Nicht genug Punkte' : 'Nicht verfügbar'}
                          </span>
                        );
                      }
                    })()}
                  </div>
                </div>
              </div>
            ))}
          
          {event.dates.filter(date => date.isActive && new Date(date.date) > new Date()).length === 0 && (
            <div style={{ 
              color: 'var(--text-light)',
              fontStyle: 'italic',
              textAlign: 'center',
              padding: '1rem'
            }}>
              Derzeit keine verfügbaren Termine
            </div>
          )}
        </div>
      </div>
    </div>
  );

  if (loading && events.length === 0) {
    return (
      <div className="container">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Lädt...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <header className="app-header">
        <div className="header-content">
          <div className="user-info">
            <span>Willkommen, {user.username}!</span>
            <span>💎 {points} Punkte</span>
            <button onClick={onLogout} className="btn btn-logout">
              Abmelden
            </button>
          </div>
        </div>
      </header>

      <div className="container">
        {/* Message Alert */}
        {message.text && (
          <div className={`alert alert-${message.type} alert-dismissible`} role="alert">
            {message.text}
            <button type="button" className="btn-close" onClick={dismissMessage}></button>
          </div>
        )}

        {/* Navigation Tabs */}
        <div className="nav-tabs">
          <button 
            className={`nav-tab ${activeTab === 'events' ? 'active' : ''}`}
            onClick={() => setActiveTab('events')}
          >
            Verfügbare Events
          </button>
          <button 
            className={`nav-tab ${activeTab === 'bookings' ? 'active' : ''}`}
            onClick={() => setActiveTab('bookings')}
          >
            Meine Buchungen
          </button>
          <button 
            className={`nav-tab ${activeTab === 'points' ? 'active' : ''}`}
            onClick={() => setActiveTab('points')}
          >
            Punktestand
          </button>
          <button 
            className={`nav-tab ${activeTab === 'coupons' ? 'active' : ''}`}
            onClick={() => setActiveTab('coupons')}
          >
            Coupons
          </button>
          {user.isAdmin && (
            <button 
              className={`nav-tab ${activeTab === 'admin' ? 'active' : ''}`}
              onClick={() => setActiveTab('admin')}
            >
              Admin
            </button>
          )}
        </div>

        {/* Tab Content */}
        {activeTab === 'events' && (
          <div className="tab-content">
            <h2>Verfügbare Events</h2>
            {events.filter(event => 
              event.dates.some(date => date.isActive && new Date(date.date) > new Date())
            ).length === 0 ? (
              <div className="text-center">
                <p>Keine Events verfügbar.</p>
              </div>
            ) : (
              events
                .filter(event => 
                  event.dates.some(date => date.isActive && new Date(date.date) > new Date())
                )
                .map(renderEventCard)
            )}
          </div>
        )}

        {activeTab === 'bookings' && (
          <div className="tab-content">
            <h2>Meine Buchungen</h2>
            {userBookings.length === 0 ? (
              <div className="text-center">
                <p>Sie haben noch keine Events gebucht.</p>
              </div>
            ) : (
              userBookings.map((booking, index) => (
                <div key={index} className="card" style={{ marginBottom: '1rem' }}>
                  <div className="card-body">
                    <h5>{booking.event.name}</h5>
                    <p><strong>Termin:</strong> {formatDate(booking.event.date)}</p>
                    <p><strong>Punkte:</strong> {booking.event.pointsRequired}</p>
                    <p><strong>Status:</strong> 
                      <span className={`badge ms-2 ${
                        booking.status === 'confirmed' ? 'bg-success' :
                        booking.status === 'waitlisted' ? 'bg-warning' : 'bg-secondary'
                      }`}>
                        {booking.status === 'confirmed' ? 'Bestätigt' :
                         booking.status === 'waitlisted' ? `Warteliste (Position ${booking.waitlistPosition})` : 
                         'Unbekannt'}
                      </span>
                    </p>
                    <div className="event-actions">
                      {canCancelBooking(booking.event.date) ? (
                        <button
                          className="btn btn-danger"
                          onClick={() => handleCancelBooking(booking.event.id, booking.event.dateId)}
                          disabled={loading}
                        >
                          Stornieren
                        </button>
                      ) : (
                        <div>
                          <small className="text-muted">
                            Stornierung nur bis 48 Stunden vor Event möglich
                          </small>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {activeTab === 'points' && (
          <div className="tab-content">
            <div className="row">
              <div className="col-md-6">
                <div className="card">
                  <div className="card-header">
                    Aktueller Punktestand
                  </div>
                  <div className="card-body text-center">
                    <h2 className="text-primary">💎 {points} Punkte</h2>
                  </div>
                </div>
              </div>
            </div>

            <h3>Punktehistorie</h3>
            {pointHistory.length === 0 ? (
              <div className="text-center">
                <p>Keine Punktehistorie verfügbar.</p>
              </div>
            ) : (
              <div className="card">
                <div className="card-body">
                  {pointHistory.map((entry, index) => (
                    <div key={index} className="d-flex justify-content-between align-items-center border-bottom py-2">
                      <div>
                        <strong>{entry.reason}</strong>
                        <br />
                        <small className="text-muted">
                          {new Date(entry.date).toLocaleDateString('de-DE')}
                        </small>
                      </div>
                      <div className={`text-${entry.amount >= 0 ? 'success' : 'danger'}`}>
                        {entry.amount >= 0 ? '+' : ''}{entry.amount} Punkte
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'coupons' && (
          <div className="tab-content">
            <div className="card">
              <div className="card-header">
                Coupon einlösen
              </div>
              <div className="card-body">
                <form onSubmit={handleApplyCoupon}>
                  <div className="form-group">
                    <label htmlFor="couponCode" className="form-label">Coupon-Code:</label>
                    <input
                      type="text"
                      id="couponCode"
                      className="form-control"
                      value={couponCode}
                      onChange={(e) => setCouponCode(e.target.value)}
                      placeholder="Coupon-Code eingeben"
                      required
                    />
                  </div>
                  <button type="submit" className="btn btn-primary" disabled={loading}>
                    Einlösen
                  </button>
                </form>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'admin' && user.isAdmin && (
          <div className="tab-content">
            <AdminPanel user={user} />
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;
